"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import type { RealtimeChannel } from "@supabase/supabase-js";

import {
  addConversationMembers,
  createDirectConversation,
  createGroupConversation,
  deleteOwnMessage,
  getConversationMessages,
  getMessageById,
  listMyConversations,
  markConversationRead,
  removeChatAttachment,
  removeConversationMember,
  searchChatUsers,
  sendChatMessage,
  updateGroupConversation,
  uploadChatAttachment,
} from "@/lib/chat/api";
import {
  MESSAGE_PAGE_SIZE,
  openChatConversationId,
  type ChatConversation,
  type ChatMessage,
  type ChatMessageType,
} from "@/lib/chat/types";
import { queryKeys } from "@/lib/query-keys";
import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import { getErrorMessage } from "@/lib/utils";

function flattenMessages(
  data: InfiniteData<ChatMessage[]> | undefined,
) {
  return data ? [...data.pages].reverse().flat() : [];
}

function patchOpenConversation(
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  message?: ChatMessage,
) {
  queryClient.setQueryData<ChatConversation[]>(
    queryKeys.chatConversations,
    (current) =>
      current?.map((conversation) => {
        if (conversation.id !== conversationId) {
          return conversation;
        }
        return {
          ...conversation,
          unreadCount: 0,
          updatedAt: message?.createdAt ?? conversation.updatedAt,
          lastMessage: message
            ? {
                id: message.id,
                body: message.body,
                type: message.messageType,
                createdAt: message.createdAt,
                senderId: message.senderId,
                deleted: Boolean(message.deletedAt),
              }
            : conversation.lastMessage,
        };
      }),
  );
}

export function useChatConversations(
  currentUserId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.chatConversations,
    queryFn: () => listMyConversations(currentUserId!),
    enabled: enabled && Boolean(currentUserId),
  });
}

export function useChatUnreadCount(
  currentUserId: string | null,
  enabled: boolean,
) {
  const pathname = usePathname();
  const openId = openChatConversationId(pathname);
  const query = useChatConversations(currentUserId, enabled);
  const unreadCount = (query.data ?? []).reduce((sum, conversation) => {
    if (openId && conversation.id === openId) {
      return sum;
    }
    return sum + Math.max(0, conversation.unreadCount);
  }, 0);
  return { ...query, unreadCount };
}

export function useChatUnreadRealtime(
  userId: string | null,
  enabled: boolean,
) {
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const openId = openChatConversationId(pathname);
  const openIdRef = useRef(openId);
  openIdRef.current = openId;

  useEffect(() => {
    if (!userId || !enabled) {
      return;
    }

    const supabase = getSupabaseBrowserClient();
    let debounce: number | undefined;

    function refreshUnread() {
      if (debounce !== undefined) {
        window.clearTimeout(debounce);
      }
      debounce = window.setTimeout(() => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.chatConversations,
        });
      }, 300);
    }

    const channel = supabase
      .channel(`chat-unread:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
        },
        (payload) => {
          const conversationId =
            payload.new && typeof payload.new === "object"
              ? (payload.new as { conversation_id?: string }).conversation_id
              : undefined;
          if (conversationId && conversationId === openIdRef.current) {
            patchOpenConversation(queryClient, conversationId);
            return;
          }
          refreshUnread();
        },
      )
      .subscribe();

    return () => {
      if (debounce !== undefined) {
        window.clearTimeout(debounce);
      }
      void supabase.removeChannel(channel);
    };
  }, [enabled, queryClient, userId]);
}

export function useChatUserSearch(search: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.chatUserSearch(search),
    queryFn: () => searchChatUsers(search),
    enabled: enabled && search.trim().length >= 2,
  });
}

export function useConversationMessages(
  conversationId: string | null,
  enabled: boolean,
) {
  return useInfiniteQuery({
    queryKey: queryKeys.chatMessages(conversationId ?? ""),
    queryFn: ({ pageParam }) =>
      getConversationMessages({
        conversationId: conversationId!,
        before: pageParam,
      }),
    enabled: enabled && Boolean(conversationId),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      if (!lastPage || lastPage.length < MESSAGE_PAGE_SIZE) {
        return undefined;
      }
      return lastPage[0]?.createdAt;
    },
  });
}

export function useChatMessages(conversationId: string | null, enabled: boolean) {
  const query = useConversationMessages(conversationId, enabled);
  const messages = useMemo(
    () => flattenMessages(query.data),
    [query.data],
  );
  return { ...query, messages };
}

export function useConversationRealtime(
  conversationId: string | null,
  enabled: boolean,
) {
  const queryClient = useQueryClient();
  const [connectionError, setConnectionError] = useState<string | null>(null);

  useEffect(() => {
    if (!conversationId || !enabled) {
      return;
    }

    const activeConversationId = conversationId;
    let cancelled = false;
    let channel: RealtimeChannel | null = null;
    const supabase = getSupabaseBrowserClient();
    const queryKey = queryKeys.chatMessages(activeConversationId);

    async function applyChange(
      eventType: string,
      messageId: string,
    ) {
      if (eventType === "DELETE") {
        queryClient.setQueryData<InfiniteData<ChatMessage[]>>(queryKey, (current) => {
          if (!current) {
            return current;
          }
          return {
            ...current,
            pages: current.pages.map((page) =>
              page.filter((message) => message.id !== messageId),
            ),
          };
        });
        return;
      }

      const message = await getMessageById(messageId);
      if (!message || cancelled) {
        return;
      }

      queryClient.setQueryData<InfiniteData<ChatMessage[]>>(queryKey, (current) => {
        if (!current) {
          return {
            pages: [[message]],
            pageParams: [undefined],
          };
        }

        const exists = current.pages.some((page) =>
          page.some((item) => item.id === message.id),
        );

        if (exists) {
          return {
            ...current,
            pages: current.pages.map((page) =>
              page.map((item) => (item.id === message.id ? message : item)),
            ),
          };
        }

        const pages = current.pages.map((page) => [...page]);
        if (pages.length === 0) {
          pages.push([message]);
        } else {
          pages[0] = [...pages[0], message];
        }

        return { ...current, pages };
      });

      patchOpenConversation(queryClient, activeConversationId, message);
    }

    channel = supabase
      .channel(`chat-messages:${activeConversationId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const row = (payload.new ?? payload.old) as { id?: string } | null;
          if (!row?.id) {
            return;
          }
          void applyChange(payload.eventType, row.id);
        },
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          setConnectionError(null);
          void queryClient.invalidateQueries({ queryKey });
          return;
        }
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          setConnectionError(
            "A valós idejű kapcsolat megszakadt. Az üzenetek frissítése késhet.",
          );
        }
      });

    return () => {
      cancelled = true;
      if (channel) {
        void supabase.removeChannel(channel);
      }
    };
  }, [conversationId, enabled, queryClient]);

  return connectionError;
}

export function useMarkConversationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markConversationRead,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.chatConversations,
      });
    },
  });
}

export function useCreateDirectConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createDirectConversation,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.chatConversations,
      });
    },
  });
}

export function useCreateGroupConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { title: string; memberIds: string[] }) =>
      createGroupConversation({
        title: input.title,
        memberIds: input.memberIds,
        avatarPath: null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.chatConversations,
      });
    },
  });
}

export function useSendChatMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userId: string;
      body: string;
      messageType: ChatMessageType;
      replyToMessageId?: string | null;
      file?: File | null;
    }) => {
      let attachment = null;
      if (input.file) {
        attachment = await uploadChatAttachment({
          userId: input.userId,
          conversationId,
          file: input.file,
        });
      }
      try {
        const sent = await sendChatMessage({
          conversationId,
          senderId: input.userId,
          body: input.body,
          messageType: input.messageType,
          replyToMessageId: input.replyToMessageId,
          attachment,
        });
        await markConversationRead({
          conversationId,
          userId: input.userId,
        });
        return sent;
      } catch (error) {
        if (attachment) {
          const cleanupError = await removeChatAttachment(attachment.path);
          if (cleanupError) {
            throw new Error(
              `${getErrorMessage(error)} A csatolmány takarítása is sikertelen: ${getErrorMessage(cleanupError)}`,
            );
          }
        }
        throw error;
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.chatMessages(conversationId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.chatConversations,
        }),
      ]);
      patchOpenConversation(queryClient, conversationId);
    },
  });
}

export function useDeleteChatMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteOwnMessage,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.chatMessages(conversationId),
      });
    },
  });
}

export function useManageConversationMembers(conversationId: string) {
  const queryClient = useQueryClient();
  return {
    add: useMutation({
      mutationFn: (memberIds: string[]) =>
        addConversationMembers({ conversationId, memberIds }),
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.chatConversations,
        });
      },
    }),
    remove: useMutation({
      mutationFn: (userId: string) =>
        removeConversationMember({ conversationId, userId }),
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.chatConversations,
        });
      },
    }),
    rename: useMutation({
      mutationFn: (title: string) =>
        updateGroupConversation({ conversationId, title }),
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.chatConversations,
        });
      },
    }),
  };
}
